<script setup lang="ts">
import logoPlaceholder from 'frontend/_assets/trophy_icon.png'
import { RouteName } from 'frontend/_setup/setupRouter/types/RouteName'
import {
  checkAccessToEventRoute,
  getEventOrganizationUser,
  isAdmin as checkIfIsAdmin,
} from 'frontend/common/access-helpers'
import { useDateFormat } from 'frontend/common/composables/useDateFormat'
import SidebarNavigationSectionItem from 'frontend/common/layout/components/sidebar-navigation-section-item.vue'
import { useCurrentEventActions } from 'frontend/common/layout/composables/use-current-event-actions'
import { getSidebarNavigationItems } from 'frontend/common/layout/constants/sidebar-navigation-items'
import { SidebarNavigationSectionItems } from 'frontend/common/layout/constants/sidebar-navigation-section-items'
import { type ISidebarNavigationItem } from 'frontend/common/layout/types/i-sidebar-navigation-item'
import { useSettingsSection } from 'frontend/events/event-dashboard-item-sections/settings'
import ContextMenuContentComponent from 'frontend/ui/components/ContextMenuContentComponent.vue'
import ContextMenuItemComponent from 'frontend/ui/components/ContextMenuItemComponent.vue'
import ContextMenuItemGroupComponent from 'frontend/ui/components/ContextMenuItemGroupComponent.vue'
import ContextMenuSeparatorComponent from 'frontend/ui/components/ContextMenuSeparatorComponent.vue'
import DropdownComponent from 'frontend/ui/components/DropdownComponent.vue'
import { computed } from 'vue'
import { type RouteLocationNormalizedLoaded, useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import SidebarItem from './sidebar-item.vue'

interface Props {
  isMenuToggled: boolean
}
defineProps<Props>()

interface Emits {
  (e: 'update:isMenuToggled', value: boolean): void
}
const emit = defineEmits<Emits>()

const store = useStore()
const router = useRouter()
const { currentEventActions, shouldShowEventDropdownActions } = useCurrentEventActions()

const slug = computed(() => {
  return useRoute().params.eventSlug
})

const eou = computed(() => {
  return getEventOrganizationUser(store.state?.session?.user, slug.value)
})
const settingsSection = useSettingsSection(eou, store.state?.session?.user)

// TODO(pieczor): why is this ignored, what if there is no settings accessible?
const _hasAccessToSettings = computed(() => {
  return !settingsSection.isHidden && (settingsSection.items?.length || 0) > 0
})

const _isAdmin = computed(() => {
  return eou.value && checkIfIsAdmin(eou.value)
})

const filteredItems = computed<ISidebarNavigationItem[]>(() => {
  return getSidebarNavigationItems(slug.value).filter(item => {
    const { path } = router.resolve(item.link)
    return checkAccessToEventRoute(eou.value, path, slug.value)
  })
})

const sectionItems = computed(() => {
  return SidebarNavigationSectionItems.map(section => {
    return {
      name: section.name,
      sectionType: section.sectionType,
      items: filteredItems.value.filter(item => {
        return item.sectionType === section.sectionType
      }),
    }
  }).filter(sectionItem => {
    return sectionItem.items.length
  })
})

const currentEvent = computed(() => {
  return store.getters['currentContext/currentEvent']
})

const eventLogo = computed(() => {
  return currentEvent.value?.logoUrls?.max40x40
    ? `/api${currentEvent.value?.logoUrls?.max40x40}`
    : logoPlaceholder
})

const hasLogo = computed(() => {
  return !!currentEvent.value?.logoUrls?.max40x40
})

function _openDashboardSettings() {
  router.push({
    name: RouteName.EventDashboard,
    query: {
      searchTerm: 'Settings',
    },
  })
}

const { getDateFormatString } = useDateFormat()

const eventTooltipOptions = computed(() => {
  const { startDate, endDate } = currentEvent.value
  let tooltipMessage = currentEvent.value.name || 'Current event'
  if (startDate && endDate) {
    const dateFmt = getDateFormatString('dateWithShortMonth')
    const formattedDates = [startDate, endDate].map(date => {
      return dayjs(date).format(dateFmt)
    })
    tooltipMessage += `<br>${formattedDates[0]} - ${formattedDates[1]}`
  }
  return {
    title: tooltipMessage,
    placement: 'bottom',
    html: true,
  }
})
</script>

<template lang="pug">
.sidebar(:class="{ 'is-toggled': isMenuToggled }")
  .sticky-wrapper
    .sticky-wrapper-content
      .current-event-wrapper(v-if="currentEvent" v-tooltip.options="eventTooltipOptions")
        .thumbnail
          img.image(v-if="hasLogo" :src="eventLogo")
          img.image.no-logo(v-else :src="eventLogo")
        .details
          .name {{ currentEvent.name }}
          .description Operational portal
        dropdown-component(v-if="shouldShowEventDropdownActions")
          template(#default="{ isOpen }")
            .btn.btn-text.btn-md.only-icon.dropdown-button(:class="{ 'is-dropdown-open': isOpen }")
              i.mi-outlined.mi-more-vert
          template(#dropdown)
            context-menu-content-component
              context-menu-item-group-component
                .dropdown-title Click item to sync event resource
              context-menu-separator-component
              context-menu-item-group-component
                context-menu-item-component(
                  v-for="(action, index) in currentEventActions"
                  :key="index"
                  @click="action.clickFn"
                  iconClass="mi-outlined mi-sync"
                  reverse
                ) {{ action.title }}
      .navigation-items
        sidebar-item(
          v-if="!currentEvent"
          :is-condensed="!isMenuToggled"
          iconClasses="mi-outlined mi-dashboard"
          link="/tmm-hub"
          title="Modes"
        )
        sidebar-item(
          v-if="$route.name !== 'tmm-hub' && !currentEvent"
          :is-condensed="!isMenuToggled"
          iconClasses="mi-outlined mi-emoji-events"
          link="/events"
          title="All events"
        )
        sidebar-item(
          v-if="currentEvent"
          :is-condensed="!isMenuToggled"
          :link="`/events/${$route.params.eventSlug}/dashboard`"
          iconClasses="mi-outlined mi-dashboard"
          title="Dashboard"
          :can-be-active-fn="(route: RouteLocationNormalizedLoaded) => route.query.searchTerm?.toLowerCase() !== 'settings'"
        )
    .toggle-sidebar(
      :class="{ 'is-toggled': isMenuToggled }"
      @click="emit('update:isMenuToggled', !isMenuToggled)"
    )
      i.icon.mi-outlined.mi-keyboard-arrow-right
  .items-wrapper(v-if="slug && eou")
    .section-items
      sidebar-navigation-section-item(
        v-for="sectionItem in sectionItems"
        :is-condensed="!isMenuToggled"
        :items="sectionItem.items"
        :key="sectionItem.sectionType"
        :name="sectionItem.name"
        :section-type="sectionItem.sectionType"
      )
      .mt--8
        sidebar-item(
          :is-condensed="!isMenuToggled"
          :link="`/events/${$route.params.eventSlug}/dashboard?searchTerm=Settings`"
          iconClasses="mi-outlined mi-settings"
          title="All settings"
          :can-be-active-fn="(route: RouteLocationNormalizedLoaded) => route.query.searchTerm?.toLowerCase() === 'settings'"
        )

    //template
    //  ul.nav.d-flex.flex-column.flex-shrink-0.flex-nowrap
    //    template(v-for="(item, index) of sidebarItems" :key="index")
    //      sidebar-item(
    //        :class="`${item.class || ''} ${marginIfNextSection(item, sidebarItems[index - 1])}`"
    //        :iconClasses="item.iconClasses"
    //        :link="item.link"
    //        :subscript="item.subscript"
    //        :title="item.title"
    //      )
    //
    //  ul.nav.d-flex.flex-column.flex-shrink-0.flex-nowrap.sidebar-settings(
    //    v-if="hasAccessToSettings"
    //    @click="openDashboardSettings"
    //  )
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables.scss' as themeVariables;
@import '../../../frontend_stylesheets/_variables.scss';

.sidebar,
.current-event-wrapper,
.section-items {
  width: $sidebar-width;
}

.mt--8 {
  margin-top: -8px;
}

.sidebar {
  position: fixed;
  top: 64px;
  height: calc(100vh - #{$header-height} - #{$footer-height});
  padding-top: var(--bs-gutter-x, 0.75rem);
  max-width: 100%;
  z-index: 1;
  background: map.get(themeVariables.$colors, surfaceDark);
  transition: width 0.1s ease;
  bottom: calc(#{$footer-height});
  display: grid;
  grid-template-rows: auto 1fr;
  border-right: 1px solid #b8c1cc;

  .erebus-app[data-theme='dark'] & {
    border-color: #3b434d;
  }

  @include for-size(small-and-medium-devices) {
    height: auto;
    bottom: calc(env(safe-area-inset-bottom, 0px) + #{$footer-height});
  }

  ul {
    overflow: hidden;
    &.sidebar-settings {
      margin-top: auto;
      @include for-size(small-and-medium-devices) {
        padding-bottom: 1rem;
        margin-top: 1.5rem;
      }
    }
  }

  .dropdown-toggle {
    cursor: pointer;
    &::after {
      margin-left: 0.5rem;
    }
  }
}

.dropdown-button {
  width: 32px !important;
  height: 32px;
  padding: 0;
  color: map.get(themeVariables.$colors, onSurface);
}

.dropdown-title {
  padding: 0 12px;
  font-weight: 600;
}

.sticky-wrapper {
  border-bottom: 1px solid #b8c1cc;
  padding-bottom: 8px;
  display: grid;
  position: relative;

  .erebus-app[data-theme='dark'] & {
    border-color: #3b434d;
  }
}

.sticky-wrapper-content {
  display: grid;
  gap: 8px;
  overflow-x: hidden;
}

.toggle-sidebar {
  position: absolute;
  right: -12px;
  bottom: -12px;

  width: 24px;
  height: 24px;
  border-radius: 100px;
  background: map.get(themeVariables.$specificColors, sidebarToggleMenuBackground);
  filter: drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.15)) drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.3));
  cursor: pointer;
  color: map.get(themeVariables.$colors, primary);

  .icon {
    font-size: 1.1rem;
    margin-left: 0.2rem;
  }

  &.is-toggled .icon {
    transform: rotate(180deg);
  }
}

.items-wrapper {
  @include prettyScrollbar(var(--custom-primary), #fff, 8px);
  overflow-x: hidden;
  padding: 12px 0;
}

.section-items {
  display: grid;
  gap: 8px;
  width: 100%;
}

.navigation-items {
  overflow: hidden;
}

.current-event-wrapper {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  gap: 8px;
  padding: 0 8px;
  box-sizing: border-box;
  align-items: center;
  overflow: hidden;
  width: 100%;
  transition: padding 0.1s ease;

  .sidebar.is-toggled & {
    padding: 0 12px;
  }

  .thumbnail {
    width: 32px;
    height: 32px;
    border-radius: $border-radius;
  }

  .image {
    width: 100%;
    height: 100%;
    object-fit: scale-down;
    background: white;
    border-radius: $border-radius;
    padding: 4px;

    &.no-logo {
      filter: invert(1);
      padding: 0;
      background: transparent;

      .erebus-app[data-theme='dark'] & {
        filter: none;
      }
    }
  }

  .details {
    transition: opacity 0.1s ease;
    opacity: 0;

    .sidebar.is-toggled & {
      opacity: 1;
    }
  }

  .name {
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    display: -webkit-box;
    max-width: 200px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .description {
    color: map.get(themeVariables.$colors, onSurfaceVariant);
    overflow: hidden;
    white-space: nowrap;
  }
}

.content-wrapper.is-condensed {
  .sidebar {
    width: $condensed-sidebar-width;
    .nav-link {
      span {
        visibility: hidden;
      }
      .icon-with-subicon {
        position: relative;

        .subicon {
          display: flex;
          background: var(--custom-primary);
          box-shadow: $box-inset-shadow;
          border-radius: 50%;
          align-items: center;
          justify-content: center;
          font-size: 8px;
          width: 20px;
          height: 20px;
          position: absolute;
          bottom: -5px;
          right: -10px;
          color: $white;
          line-height: 0;
        }
      }
    }
  }
}
</style>
